// utils/format.ts — Date / string / pill formatters lifted from
// the legacy console.css inline JS. Pure functions; deterministic.

import type { Timestamp } from 'firebase/firestore';

const DASH = '-';

/** YYYY-MM-DD in UTC. `-` for missing / malformed input. */
export function fmtDate(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return DASH;
  try {
    return ts.toDate().toISOString().slice(0, 10);
  } catch {
    return DASH;
  }
}

/** YYYY-MM-DD HH:MM:SSZ (UTC). Audit-log absolute timestamp. */
export function fmtAbs(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return DASH;
  try {
    const iso = ts.toDate().toISOString();
    return `${iso.slice(0, 10)} ${iso.slice(11, 19)}Z`;
  } catch {
    return DASH;
  }
}

/** Human relative duration since the timestamp. */
export function fmtRel(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toMillis !== 'function') return DASH;
  const diff = Date.now() - ts.toMillis();
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/** "in 7d" / "in 4h" / "expired". For invite-expiry rendering. */
export function fmtExpiresIn(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toMillis !== 'function') return DASH;
  const diffMs = ts.toMillis() - Date.now();
  if (diffMs <= 0) return 'expired';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days >= 1) return `in ${days}d`;
  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  if (hours >= 1) return `in ${hours}h`;
  const mins = Math.max(1, Math.floor(diffMs / 60000));
  return `in ${mins}m`;
}

/** Privacy-safe member label: '#' + first 6 chars of uid, upper-case. */
export function memberLabel(uid: string | null | undefined): string {
  return `#${String(uid ?? '').slice(0, 6).toUpperCase()}`;
}
